/* eslint-disable no-console */
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { AiMappingNotify } from "./aiMappingControllerTypes";
import { LatLngPair } from "./aiMappingGeo";
import { AiMappingMapDriver, AiMappingResultsLike } from "./aiMappingTypes";

interface UseAiMappingMeasuredAreaToolsOptions {
  driver: AiMappingMapDriver | null;
  notify: AiMappingNotify;
  resetKey: string | null;
  splittingSegmentationId: string | null;
  setSplittingSegmentationId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedAreaId: string | null;
  selectedSegmentationId: string | null;
  allResults: AiMappingResultsLike[];
  selectArea: (areaId: string | null) => Promise<void>;
  selectSegmentation: (segmentationId: string | null) => void;
  splitSegmentation: (areaId: string, segmentationId: string) => void;
  splitAreasAlongLine: (
    line: LatLngPair[],
    targetSegmentationId?: string | null
  ) => Promise<number>;
  eraseAreasAlongPolygon: (points: LatLngPair[]) => Promise<number>;
  mergeAreasAlongPolygon: (points: LatLngPair[]) => Promise<number>;
  onSplitLine?: (line: LatLngPair[]) => number;
  onErasePolygon?: (points: LatLngPair[]) => number;
  onMergePolygon?: (points: LatLngPair[]) => number;
}

export function useAiMappingMeasuredAreaTools({
  driver,
  notify,
  resetKey,
  splittingSegmentationId,
  setSplittingSegmentationId,
  selectedAreaId,
  selectedSegmentationId,
  allResults,
  selectArea,
  selectSegmentation,
  splitSegmentation,
  splitAreasAlongLine,
  eraseAreasAlongPolygon,
  mergeAreasAlongPolygon,
  onSplitLine,
  onErasePolygon,
  onMergePolygon,
}: UseAiMappingMeasuredAreaToolsOptions) {
  const [splitModeActive, setSplitModeActive] = useState(false);
  const [mergeModeActive, setMergeModeActive] = useState(false);
  const [eraseModeActive, setEraseModeActive] = useState(false);

  const cancelModes = useCallback(() => {
    setSplitModeActive(false);
    setSplittingSegmentationId(null);
    setMergeModeActive(false);
    setEraseModeActive(false);
  }, []);

  useEffect(() => {
    cancelModes();
  }, [resetKey, cancelModes]);

  const onSegmentationClick = useCallback(
    (segmentationId: string, areaId: string) => {
      if (splitModeActive) {
        if (!splittingSegmentationId) {
          void splitSegmentation(areaId, segmentationId);
        }
        return;
      }
      if (String(areaId) !== String(selectedAreaId)) {
        void (async () => {
          await selectArea(areaId);
          selectSegmentation(segmentationId);
        })();
        return;
      }
      selectSegmentation(segmentationId);
    },
    [
      splitModeActive,
      splittingSegmentationId,
      selectedAreaId,
      selectArea,
      selectSegmentation,
      splitSegmentation,
    ]
  );

  const toggleMergeMode = useCallback(() => {
    if (mergeModeActive) {
      setMergeModeActive(false);
      driver?.cancelDraw();
      driver?.clearDraft();
      return;
    }
    if (!driver) {
      notify("The map is still loading — try again in a moment.", "warning");
      return;
    }
    setSplitModeActive(false);
    setEraseModeActive(false);
    setMergeModeActive(true);
    notify(
      "Draw a polygon around the measured areas to merge them — double-click to finish.",
      "info"
    );
    driver.startDraw(
      "POLYGON",
      (points) => {
        void (async () => {
          try {
            driver?.clearDraft();
            const manualCount = onMergePolygon?.(points) ?? 0;
            const aiCount = await mergeAreasAlongPolygon(points);
            const total = manualCount + aiCount;
            if (total === 0) {
              notify("The merge polygon must touch a measured area to merge it.", "info");
              return;
            }
            notify(
              `Merged ${total} area${
                total === 1 ? "" : "s"
              } — change each area's color to define phases`,
              "success"
            );
          } finally {
            driver?.clearDraft();
            setMergeModeActive(false);
          }
        })().catch((error) => console.error("[ai-mapping] merge failed", error));
      },
      () => {
        driver?.clearDraft();
        setMergeModeActive(false);
      },
      { showDraft: false }
    );
  }, [driver, mergeModeActive, notify, mergeAreasAlongPolygon, onMergePolygon]);

  const toggleEraseMode = useCallback(() => {
    if (eraseModeActive) {
      setEraseModeActive(false);
      driver?.cancelDraw();
      driver?.clearDraft();
      return;
    }
    if (!driver) {
      notify("The map is still loading — try again in a moment.", "warning");
      return;
    }
    setSplitModeActive(false);
    setMergeModeActive(false);
    setEraseModeActive(true);
    notify(
      "Draw a polygon over the measured areas to erase them — double-click to finish.",
      "info"
    );
    driver.startDraw(
      "POLYGON",
      (points) => {
        void (async () => {
          try {
            driver?.clearDraft();
            const manualCount = onErasePolygon?.(points) ?? 0;
            const aiCount = await eraseAreasAlongPolygon(points);
            if (manualCount + aiCount === 0) {
              notify("The erase polygon must overlap a measured area to erase it.", "info");
            }
          } finally {
            driver?.clearDraft();
            setEraseModeActive(false);
          }
        })().catch((error) => console.error("[ai-mapping] erase failed", error));
      },
      () => {
        driver?.clearDraft();
        setEraseModeActive(false);
      },
      { showDraft: false }
    );
  }, [driver, eraseModeActive, notify, eraseAreasAlongPolygon, onErasePolygon]);

  const toggleSplitMode = useCallback(() => {
    if (splitModeActive) {
      setSplitModeActive(false);
      setSplittingSegmentationId(null);
      driver?.cancelDraw();
      return;
    }
    const targetSegmentationId = selectedSegmentationId;
    setMergeModeActive(false);
    setEraseModeActive(false);
    setSplitModeActive(true);
    driver?.startDraw(
      "LINE",
      (points) => {
        void (async () => {
          try {
            const manualCount = onSplitLine?.(points) ?? 0;
            const aiCount = await splitAreasAlongLine(points, targetSegmentationId);
            const total = manualCount + aiCount;
            if (total === 0) {
              notify(
                "The split line must cross all the way through a measured area to split it.",
                "info"
              );
              return;
            }
            notify(
              `Split into ${total} area${
                total === 1 ? "" : "s"
              } — change each area's color to define phases`,
              "success"
            );
          } finally {
            setSplitModeActive(false);
            setSplittingSegmentationId(null);
          }
        })().catch((error) => console.error("[ai-mapping] split failed", error));
      },
      () => {
        setSplitModeActive(false);
        setSplittingSegmentationId(null);
      }
    );
  }, [driver, splitModeActive, selectedSegmentationId, splitAreasAlongLine, notify, onSplitLine]);

  return {
    splitModeActive,
    splittingSegmentationId,
    mergeModeActive,
    eraseModeActive,
    onSegmentationClick,
    toggleMergeMode,
    toggleEraseMode,
    toggleSplitMode,
    cancelModes,
  };
}
